import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, User, Mail, Phone, MapPin, Percent, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface PartnerAccount {
  id: string;
  user_id: string;
  display_name: string | null;
  display_email: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  secondary_email: string | null;
  performance_fee_pct: number;
  notes: string | null;
  profiles: { full_name: string; email: string } | null;
}

interface EditPartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  partner: PartnerAccount | null;
  onUpdateComplete: () => void;
}

const EditPartnerModal: React.FC<EditPartnerModalProps> = ({
  isOpen,
  onClose,
  partner,
  onUpdateComplete,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [performanceFeePct, setPerformanceFeePct] = useState('');
  const [notes, setNotes] = useState('');

  // Load partner data when modal opens
  // Use display_name/display_email if set, otherwise fall back to profiles
  useEffect(() => {
    if (partner) {
      setFullName(partner.display_name || partner.profiles?.full_name || '');
      setEmail(partner.display_email || partner.profiles?.email || '');
      setPhone(partner.phone || '');
      setSecondaryEmail(partner.secondary_email || '');
      setAddressLine1(partner.address_line1 || '');
      setAddressLine2(partner.address_line2 || '');
      setCity(partner.city || '');
      setState(partner.state || '');
      setPostalCode(partner.postal_code || '');
      setCountry(partner.country || 'USA');
      setPerformanceFeePct(partner.performance_fee_pct.toString());
      setNotes(partner.notes || '');
    }
  }, [partner]);

  const handleSave = async () => {
    if (!partner) return;

    // Validate required fields
    if (!fullName.trim()) {
      toast({
        title: "Validation Error",
        description: "Full name is required.",
        variant: "destructive",
      });
      return;
    }

    if (!email.trim()) {
      toast({
        title: "Validation Error",
        description: "Email is required.",
        variant: "destructive",
      });
      return;
    }

    const feeValue = parseFloat(performanceFeePct);
    if (isNaN(feeValue) || feeValue < 0 || feeValue > 100) {
      toast({
        title: "Validation Error",
        description: "Performance fee must be between 0 and 100.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Update partner_accounts table with display fields
      // This does NOT change the partner's login credentials
      const { error: accountError } = await supabase
        .from('partner_accounts')
        .update({
          display_name: fullName.trim() || null,
          display_email: email.trim().toLowerCase() || null,
          phone: phone.trim() || null,
          secondary_email: secondaryEmail.trim() || null,
          address_line1: addressLine1.trim() || null,
          address_line2: addressLine2.trim() || null,
          city: city.trim() || null,
          state: state.trim() || null,
          postal_code: postalCode.trim() || null,
          country: country.trim() || null,
          performance_fee_pct: feeValue,
          notes: notes.trim() || null,
          last_updated: new Date().toISOString(),
        })
        .eq('id', partner.id);

      if (accountError) throw accountError;

      toast({
        title: "Partner Updated",
        description: `Successfully updated details for ${fullName}.`,
      });

      onUpdateComplete();
      onClose();
    } catch (error: any) {
      console.error('Error updating partner:', error);
      toast({
        title: "Update Failed",
        description: error.message || "An error occurred while updating the partner.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!partner) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-delphi-dark border-delphi-gray/30 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl text-delphi-light">
            Edit Partner Details
          </DialogTitle>
          <DialogDescription className="text-delphi-light/60">
            Update display information for {partner.display_name || partner.profiles?.full_name || 'this partner'}.
            <span className="block mt-1 text-xs text-delphi-light/40">
              Note: This does not change the partner's login credentials.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-delphi-light/70 flex items-center gap-2">
              <User size={16} className="text-delphi-red" />
              Display Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-delphi-light">Display Name *</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-delphi-light">Display Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                  placeholder="john@example.com"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-delphi-light/70 flex items-center gap-2">
              <Phone size={16} className="text-delphi-red" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-delphi-light">Phone Number</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondaryEmail" className="text-delphi-light">Secondary Email</Label>
                <Input
                  id="secondaryEmail"
                  type="email"
                  value={secondaryEmail}
                  onChange={(e) => setSecondaryEmail(e.target.value)}
                  className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                  placeholder="alternate@example.com"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-delphi-light/70 flex items-center gap-2">
              <MapPin size={16} className="text-delphi-red" />
              Address
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="addressLine1" className="text-delphi-light">Address Line 1</Label>
                <Input
                  id="addressLine1"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                  placeholder="123 Main Street"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="addressLine2" className="text-delphi-light">Address Line 2</Label>
                <Input
                  id="addressLine2"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                  placeholder="Suite 100"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-delphi-light">City</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                    placeholder="New York"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state" className="text-delphi-light">State</Label>
                  <Input
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                    placeholder="NY"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postalCode" className="text-delphi-light">Postal Code</Label>
                  <Input
                    id="postalCode"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                    placeholder="10001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country" className="text-delphi-light">Country</Label>
                  <Input
                    id="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                    placeholder="USA"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Account Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-delphi-light/70 flex items-center gap-2">
              <Percent size={16} className="text-delphi-red" />
              Account Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="performanceFeePct" className="text-delphi-light">Performance Fee (%)</Label>
                <Input
                  id="performanceFeePct"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={performanceFeePct}
                  onChange={(e) => setPerformanceFeePct(e.target.value)}
                  className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                  placeholder="20"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-delphi-light/70 flex items-center gap-2">
              <FileText size={16} className="text-delphi-red" />
              Notes
            </h3>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light min-h-[100px]"
              placeholder="Internal notes about this partner..."
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-delphi-gray/30 text-delphi-light hover:bg-delphi-gray/10"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-delphi-red hover:bg-delphi-red/90 text-white"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditPartnerModal;
